import { useState } from 'react';
import { admin, type AdminJobItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { DataTable, StatusBadge, Tabs, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

const STATUSES = ['all', 'running', 'failed', 'partial', 'succeeded', 'pending', 'cancelled'];

export default function AdminJobsPage() {
  const t = useAdminT();
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState<AdminJobItem | null>(null);

  const q = useApi(() => admin.jobs(status === 'all' ? undefined : status, 100), [status]);
  const rows = q.status === 'success' ? q.data : [];

  const columns: Column<AdminJobItem>[] = [
    { key: 'book_id', header: t('col.book'), width: '110px', mono: true,
      render: (r) => r.book_id.slice(0, 8) },
    { key: 'status', header: t('col.status'), width: '110px',
      render: (r) => <StatusBadge status={r.status} /> },
    { key: 'stage', header: t('jobs.stage'), render: (r) => r.stage ?? '—' },
    { key: 'progress', header: '%', width: '64px', align: 'right', mono: true,
      render: (r) => `${Math.round((r.progress ?? 0) * 100)}` },
    { key: 'total_cost_usd', header: t('jobs.cost'), width: '90px', align: 'right', mono: true,
      render: (r) => `$${Number(r.total_cost_usd ?? 0).toFixed(2)}` },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('jobs.title')}</h1>
        <p className="admin-page__sub">{t('jobs.subtitle')}</p>
      </header>

      <div className="admin-toolbar">
        <Tabs items={STATUSES.map((s) => ({ key: s, label: s }))} value={status} onChange={setStatus} />
      </div>

      <div className="admin-md">
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id}
          onRowClick={setSelected} selectedKey={selected?.id ?? null}
          loading={q.status === 'loading'} empty={t('jobs.none')} />

        <aside className="admin-detail">
          {!selected ? (
            <p className="admin-detail__hint">{t('jobs.selectHint')}</p>
          ) : (
            <>
              <h3>{selected.book_id.slice(0, 8)} · {selected.status}</h3>
              <dl className="admin-kv">
                <dt>{t('jobs.stage')}</dt><dd>{selected.stage ?? '—'}</dd>
                <dt>{t('jobs.cost')}</dt><dd>${Number(selected.total_cost_usd ?? 0).toFixed(2)}</dd>
                <dt>{t('jobs.calls')}</dt><dd>{selected.total_calls}</dd>
              </dl>
              {selected.error && (
                <>
                  <div className="admin-field__label">{t('jobs.error')}</div>
                  <pre className="admin-json">{JSON.stringify(selected.error, null, 2)}</pre>
                </>
              )}
              {selected.degradations && selected.degradations.length > 0 && (
                <>
                  <div className="admin-field__label">{t('jobs.degradations')}</div>
                  <pre className="admin-json">{JSON.stringify(selected.degradations, null, 2)}</pre>
                </>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
